/**
 * Hero video: progressively enhances a poster image with a muted, looping video.
 *
 * Markup comes from templates/hero-media.php. The video has no src until we
 * decide to play it, so visitors who never see it never download it:
 * - only after window.load (the poster is the first paint);
 * - only on large screens (phones and small tablets keep the poster);
 * - only without prefers-reduced-motion and without Save-Data.
 *
 * The pause button satisfies WCAG 2.2.2; a pause is remembered across pages.
 */

const LARGE_SCREEN_QUERY = '(min-width: 55.0625rem)';
const MOTION_QUERY = '(prefers-reduced-motion: no-preference)';
const STORAGE_KEY = 'hltsamenHeroVideoPaused';

/**
 * Read the stored pause preference.
 *
 * @return {boolean}
 */
const getStoredPause = () => {
	try {
		return '1' === window.localStorage.getItem( STORAGE_KEY );
	} catch ( e ) {
		return false;
	}
};

/**
 * Store the pause preference.
 *
 * @param {boolean} paused Whether the visitor paused the video.
 */
const setStoredPause = ( paused ) => {
	try {
		if ( paused ) {
			window.localStorage.setItem( STORAGE_KEY, '1' );
		} else {
			window.localStorage.removeItem( STORAGE_KEY );
		}
	} catch ( e ) {
		// Storage unavailable (private mode, blocked); the pause still works for this page.
	}
};

/**
 * Initialise one hero media wrapper.
 *
 * @param {HTMLElement} wrapper The [data-hero-media] element.
 */
const initHeroVideo = ( wrapper ) => {
	const video = wrapper.querySelector( '.hero-media__video' );
	const button = wrapper.querySelector( '.hero-media__toggle' );
	const label = wrapper.querySelector( '.hero-media__label' );

	if ( ! video || ! button || ! video.dataset.src ) {
		return;
	}

	const largeScreen = window.matchMedia( LARGE_SCREEN_QUERY );
	const motion = window.matchMedia( MOTION_QUERY );
	let userPaused = getStoredPause();
	let inView = true;

	const isAllowed = () => largeScreen.matches && motion.matches && ! navigator.connection?.saveData;

	const syncButton = () => {
		const paused = video.paused;

		wrapper.classList.toggle( 'is-paused', paused );
		label.textContent = paused ? button.dataset.labelPlay : button.dataset.labelPause;
	};

	const play = () => {
		if ( ! video.getAttribute( 'src' ) ) {
			video.src = video.dataset.src;
		}

		// Autoplay can still be refused (e.g. iOS low power mode); the poster stays and the button offers play.
		video.play().catch( syncButton );
	};

	const hideButton = () => {
		// Hiding a focused button drops focus to <body>; move it to the hero title instead.
		if ( document.activeElement === button ) {
			wrapper.closest( '.hero' )?.querySelector( 'h1[tabindex]' )?.focus( { preventScroll: true } );
		}

		button.hidden = true;
	};

	const update = () => {
		if ( ! isAllowed() ) {
			video.pause();
			wrapper.classList.remove( 'is-playing' );
			hideButton();
			return;
		}

		button.hidden = false;

		if ( userPaused || ! inView ) {
			video.pause();
			syncButton();
			return;
		}

		play();
	};

	video.addEventListener( 'playing', () => {
		wrapper.classList.add( 'is-playing' );
		syncButton();
	} );
	video.addEventListener( 'pause', syncButton );

	button.addEventListener( 'click', () => {
		userPaused = ! video.paused;
		setStoredPause( userPaused );

		if ( userPaused ) {
			video.pause();
		} else {
			play();
		}
	} );

	largeScreen.addEventListener( 'change', update );
	motion.addEventListener( 'change', update );

	// Browsers may pause muted video in a background tab without resuming it.
	document.addEventListener( 'visibilitychange', () => {
		if ( ! document.hidden ) {
			update();
		}
	} );

	// The poster bleeds past its grid cell to the hero edge; expose that distance so the
	// pause button can sit at the visible right edge (see _hero-video.scss).
	const hero = wrapper.closest( '.hero' );

	if ( hero && 'ResizeObserver' in window ) {
		new ResizeObserver( () => {
			const bleed = hero.getBoundingClientRect().right - wrapper.parentElement.getBoundingClientRect().right;
			wrapper.style.setProperty( '--hero-media-bleed', `${ Math.max( 0, bleed ) }px` );
		} ).observe( hero );
	}

	if ( 'IntersectionObserver' in window ) {
		new IntersectionObserver( ( [ entry ] ) => {
			inView = entry.isIntersecting;
			update();
		} ).observe( wrapper );
	}

	update();
};

/**
 * Initialise all hero videos once the page (and so the poster) has loaded.
 */
const initHeroVideos = () => {
	document.querySelectorAll( '[data-hero-media]' ).forEach( initHeroVideo );
};

if ( 'complete' === document.readyState ) {
	initHeroVideos();
} else {
	window.addEventListener( 'load', initHeroVideos );
}
